import { t } from 'i18next'
import { SelectDataType } from '~/components/EFSelect/EFSelect.model'
import { IPasswordPolicy } from '~/models'
export function strToBool(str: string | null | boolean) {
  if (!str) return false
  return str.toString().toLowerCase() === 'true'
}

export function TranslateToSelectTypeArray(
  arr: Array<any> | undefined | null,
  value?: string,
  name?: string,
  description?: string
): SelectDataType[] {
  if (!arr || !Array.isArray(arr)) {
    return []
  }

  if (value && name) {
    if (description) {
      return arr.map(item => {
        const itemName = item[name]
        const itemDescription = description ? ` - ${item[description]}` : ''
        const nameWithDescription = `${itemName}${itemDescription}`

        return {
          value: item[value],
          name: item[name],
          customText: nameWithDescription
        } as SelectDataType
      })
    }

    return arr.map(item => {
      return {
        value: item[value],
        name: item[name]
      } as SelectDataType
    })
  } else
    return arr.map(item => {
      return {
        value: item,
        name: item
      } as SelectDataType
    })
}

export function camalize(str: string) {
  return str.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase())
}

export function convertEmptyStringToNull(obj: Record<string, any>): any {
  const result: Record<string, any> = {}

  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key]
      if (typeof value === 'object' && value !== null) {
        result[key] = convertEmptyStringToNull(value)
      } else if (typeof value === 'string' && value.trim() === '') {
        result[key] = null
      } else {
        result[key] = value
      }
    }
  }

  return result
}

export function convertStringToNumber(inputString: any): number | null {
  const numericValue = parseFloat(inputString)

  // Check if the conversion was successful
  if (!isNaN(numericValue)) {
    return numericValue
  } else {
    return null // Conversion failed, return null or handle the error as needed
  }
}
export function areAllElementsEqual(arr: any[]) {
  for (let i = 1; i < arr.length; i++) {
    if (arr[i] !== arr[0]) {
      return false
    }
  }
  return true
}

export function isJsonString(str: string) {
  try {
    return JSON.parse(str) && !!str
  } catch (e) {
    return false
  }
}

export function addSpaceToUpperLower(str: string) {
  return str.replace(/([a-z])([A-Z])/g, '$1 $2')
}

export function copyToClipboard(
  text: string,
  copyCallback: Function,
  errorCallback: Function
) {
  //The writeText method will return a promise then we need to use 'then()' as the callback to deal with. [EF-11169]
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard
      .writeText(text)
      .then(() => {
        if (copyCallback) {
          copyCallback()
        }
      })
      .catch(err => {
        if (errorCallback) {
          errorCallback(err)
        }
      })
  } else {
    // Fallback for older browsers
    let textArea = document.createElement('textarea')
    textArea.value = text
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()
    try {
      document.execCommand('copy')
      copyCallback()
    } catch (err) {
      errorCallback(err)
    }
    document.body.removeChild(textArea)
  }
}

export function validatePassword(
  newPassword: string,
  newConfirmPassword: string,
  passwordPolicy?: IPasswordPolicy
) {
  if (!newPassword || newPassword === '') {
    return {
      lowercase: undefined,
      uppercase: undefined,
      numeric: undefined,
      length: undefined,
      nonalphanumeric: undefined,
      match: undefined
    }
  }
  const regexLower = /[a-z]/g
  const regexUpper = /[A-Z]/g
  const regexNumber = /\d/g
  const regexNonAlphanumeric = /[^a-zA-Z0-9]/g

  const isLowercase =
    passwordPolicy &&
    (newPassword.match(regexLower) || []).length >=
    passwordPolicy?.minRequiredLowercaseCharacters
  const isUppercase =
    passwordPolicy &&
    (newPassword.match(regexUpper) || []).length >=
    passwordPolicy.minRequiredUppercaseCharacters
  const isNumeric =
    passwordPolicy &&
    (newPassword.match(regexNumber) || []).length >=
    passwordPolicy.minRequiredNumericCharacters
  const isNonAlphanumericValid =
    passwordPolicy &&
    (newPassword.match(regexNonAlphanumeric) || []).length >=
    passwordPolicy.minRequiredNonalphanumericCharacters
  const isLengthValid =
    passwordPolicy && newPassword.length >= passwordPolicy.minRequiredPasswordLength
  const passwordsMatch = newPassword === newConfirmPassword

  return {
    lowercase: isLowercase,
    uppercase: isUppercase,
    numeric: isNumeric,
    length: isLengthValid,
    nonalphanumeric: isNonAlphanumericValid,
    match: passwordsMatch
  }
}

export function showValidationMessageSingularOrPlural(
  resourceMessageKey: string,
  limitedCharacters: number
) {
  let messageContent = t(resourceMessageKey, { 0: limitedCharacters })
  if (limitedCharacters === 1 && messageContent.length > 0) {
    return messageContent.substring(0, messageContent.length - 1)
  }
  return messageContent
}

export function omit(obj: any, keys: string[]) {
  return Object.fromEntries(
    Object.entries(obj).filter(([k]) => !keys.includes(k))
  );
}